import { doc, getDoc, updateDoc, DocumentReference } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Invitation, WeddingDetails, AttendanceStatus, InvitationGuest } from '../types/Invitation';
import { FirebaseInvitation, FirebaseWedding, FirebaseGuest, FirebaseGuestList } from '../types/firebase';
import { Gift } from '../types/Gift';

/**
 * Maps Firebase guest (snake_case) to app guest (camelCase)
 */
const mapFirebaseGuest = (firebaseGuest: FirebaseGuest): InvitationGuest => ({
    firstName: firebaseGuest.first_name,
    middleName: firebaseGuest.middle_name,
    lastName: firebaseGuest.last_name,
    phoneNumber: firebaseGuest.phone_number,
    gifts: firebaseGuest.gifts || [],
});

/**
 * Maps Firebase wedding (snake_case) to app wedding details (camelCase)
 */
function mapFirebaseWedding(firebaseWedding: FirebaseWedding, guestsListName: string): WeddingDetails {
    const weddingEvent = firebaseWedding.schedule.find(event => event.title === guestsListName);
    if (!weddingEvent) {
        throw new Error('Wedding event not found');
    }
    return ({
        id: firebaseWedding.id,
        groomFirstName: firebaseWedding.groom_first_name,
        groomMiddleName: firebaseWedding.groom_middle_name || '',
        groomLastName: firebaseWedding.groom_last_name || '',
        brideFirstName: firebaseWedding.bride_first_name,
        brideMiddleName: firebaseWedding.bride_middle_name || '',
        brideLastName: firebaseWedding.bride_last_name || '',
        email: firebaseWedding.email,
        phoneNumber: firebaseWedding.phone_number,
        city: firebaseWedding.city,
        country: firebaseWedding.country,
        invitationCardPath: firebaseWedding.invitation_card_path || '',
        weddingEvent: {
            title: weddingEvent.title,
            description: weddingEvent.description,
            date_time: weddingEvent.date_time.toDate().toISOString() || '',
            location: {
                name: weddingEvent.location.name || '',
                address: weddingEvent.location.address || '',
                latitude: weddingEvent.location.latitude || 0,
                longitude: weddingEvent.location.longitude || 0,
            },
        },
    });
}

/**
 * Fetches an invitation by ID from Firestore
 * The invitation document contains a reference to the wedding document
 * which is also fetched to get full wedding details
 */
export const getInvitationById = async (invitationId: string): Promise<Invitation | null> => {
    try {
        console.log('Fetching invitation by ID:', invitationId);
        // Fetch invitation document
        const invitationRef = doc(db, 'Invitations', invitationId);
        console.log('Invitation reference:', invitationRef);
        const invitationSnap = await getDoc(invitationRef);
        console.log('Invitation snapshot:', invitationSnap);
        if (!invitationSnap.exists()) {
            console.log('Invitation not found:', invitationId);
            return null;
        }

        const firebaseInvitation = invitationSnap.data() as FirebaseInvitation;
        console.log('Firebase invitation:', firebaseInvitation);

        // Fetch guests list document using the reference
        console.log('Guests list reference:', firebaseInvitation.guests_list);
        const guestsListRef = firebaseInvitation.guests_list as DocumentReference;
        const guestsListSnap = await getDoc(guestsListRef);
        console.log('Guests list snapshot:', guestsListSnap);
        const guestsList = guestsListSnap.data() as FirebaseGuestList;
        console.log('Guests list name:', guestsList.name);

        // Fetch wedding document using the reference
        const weddingRef = firebaseInvitation.wedding as DocumentReference;
        const weddingSnap = await getDoc(weddingRef);
        console.log('Wedding snapshot:', weddingSnap);
        let weddingDetails: WeddingDetails | undefined;
        console.log('Wedding details:', weddingDetails);

        if (weddingSnap.exists()) {
            const firebaseWedding = {
                id: weddingSnap.id,
                ...weddingSnap.data(),
            } as FirebaseWedding;
            weddingDetails = mapFirebaseWedding(firebaseWedding, guestsList.name);
        }
        console.log('Wedding details:', weddingDetails);
        
        // Build the invitation object
        const invitation: Invitation = {
            id: invitationSnap.id,
            attendanceStatus: firebaseInvitation.attendance_status as AttendanceStatus,
            guest: mapFirebaseGuest(firebaseInvitation.guest),
            guestsListRef: firebaseInvitation.guests_list.path,
            weddingRef: weddingRef.path,
            weddingDetails,
        };
        console.log('Invitation:', invitation);
        return invitation;
    } catch (error) {
        console.error('Error fetching invitation:', error);
        throw error;
    }
};

/**
 * Updates the attendance status of an invitation
 */
export const updateAttendanceStatus = async (
    invitationId: string,
    status: AttendanceStatus
): Promise<void> => {
    try {
        const invitationRef = doc(db, 'Invitations', invitationId);
        await updateDoc(invitationRef, {
            attendance_status: status,
        });
        console.log('Attendance status updated to:', status);
    } catch (error) {
        console.error('Error updating attendance status:', error);
        throw error;
    }
};

/**
 * Adds a gift to an invitation's guest
 */
export const addGiftToInvitation = async (
    invitationId: string,
    gift: Gift
): Promise<void> => {
    try {
        const invitationRef = doc(db, 'Invitations', invitationId);
        const invitationSnap = await getDoc(invitationRef);

        if (!invitationSnap.exists()) {
            throw new Error('Invitation not found');
        }

        const currentData = invitationSnap.data() as FirebaseInvitation;
        const currentGifts = currentData.guest.gifts || [];

        await updateDoc(invitationRef, {
            'guest.gifts': [...currentGifts, gift],
        });

        console.log('Gift added successfully');
    } catch (error) {
        console.error('Error adding gift:', error);
        throw error;
    }
};
